import { z } from "zod/v4";

// A logged result in a league. Pending settles automatically after 48h
// unless disputed first; staff resolution actions land in a later phase.
export const leagueMatchStatusSchema = z.enum(["pending", "disputed", "confirmed", "voided"]);

export const leagueMatchPlayerSchema = z.object({
	avatar_url: z.string().nullable(),
	display_name: z.string(),
	// Only set once the match is confirmed.
	rating_change: z.number().int().nullable(),
	user_id: z.string().uuid(),
});

export const leagueMatchSetSchema = z.object({
	side1: z.number().int(),
	side2: z.number().int(),
});

export const leagueMatchSidesSchema = z.object({
	side1: z.array(leagueMatchPlayerSchema),
	side2: z.array(leagueMatchPlayerSchema),
});

export const leagueMatchConfirmedKindSchema = z.enum(["opponent", "silence", "staff"]);

export const leagueMatchConfirmedSchema = z.object({
	at: z.coerce.date(),
	kind: leagueMatchConfirmedKindSchema,
});

export const leagueMatchDisputeSchema = z.object({
	at: z.coerce.date(),
	by: z.string().uuid().nullable(),
	note: z.string().nullable(),
});

export const leagueMatchVoidReasonSchema = z.enum(["withdrawn", "staff_voided", "season_closed"]);

export const leagueMatchVoidSchema = z.object({
	at: z.coerce.date(),
	reason: leagueMatchVoidReasonSchema,
});

export const leagueMatchViewerRoleSchema = z.enum(["submitter", "teammate", "opponent", "none"]);

export const leagueMatchViewerSchema = z.object({
	can_confirm: z.boolean(),
	can_dispute: z.boolean(),
	// Staff-only resolution actions — pending/disputed/confirmed per the wire contract.
	can_edit: z.boolean(),
	can_uphold: z.boolean(),
	can_void: z.boolean(),
	can_withdraw: z.boolean(),
	role: leagueMatchViewerRoleSchema,
});

export const leagueMatchSchema = z.object({
	confirmed: leagueMatchConfirmedSchema.nullable(),
	created_at: z.coerce.date(),
	dispute: leagueMatchDisputeSchema.nullable(),
	// True once staff corrected the sets/date at least once.
	edited: z.boolean(),
	// Pending past its 48h window — settlement runs async, this just means it's due.
	expired: z.boolean(),
	expires_at: z.coerce.date(),
	id: z.string().uuid(),
	played_on: z.string(),
	// Staff's note on an uphold/void.
	resolution_note: z.string().nullable(),
	season_id: z.string().uuid(),
	sets: z.array(leagueMatchSetSchema),
	sides: leagueMatchSidesSchema,
	status: leagueMatchStatusSchema,
	submitted_by: z.string().uuid(),
	viewer: leagueMatchViewerSchema,
	void: leagueMatchVoidSchema.nullable(),
	winner_side: z.union([z.literal(1), z.literal(2)]),
});

// Newest first; `next_before` feeds back in as the `before` cursor for the next page.
export const leagueMatchListResponseSchema = z.object({
	items: z.array(leagueMatchSchema),
	next_before: z.string().nullable(),
});

// Staff resolution bodies: uphold/void take only an optional note; editing a
// result replaces its sets (and optionally its date) wholesale — the roster
// cannot change (void it and enter a new one instead).
export const resolveLeagueMatchSchema = z.object({
	note: z.string().max(500).optional(),
});

export const editLeagueMatchSchema = z.object({
	note: z.string().max(500).optional(),
	played_on: z.string().optional(),
	sets: z.array(leagueMatchSetSchema).min(1).max(5),
});

// A staff-entered result (`as_staff: true` is added by the client call) — the
// staff member need not be a player themselves.
export const enterLeagueResultSchema = z.object({
	note: z.string().max(500).optional(),
	played_on: z.string().optional(),
	season_id: z.string().uuid().optional(),
	sets: z.array(leagueMatchSetSchema).min(1).max(5),
	// 1 (singles) or 2 (doubles) user ids per side.
	side1: z.array(z.string().uuid()).min(1).max(2),
	side2: z.array(z.string().uuid()).min(1).max(2),
});
